/* eslint-disable @typescript-eslint/no-inferrable-types */
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useModalContext } from '../../provider'
import { useRouteState, getDateFormatted, getTime, parseCustomDate } from '@shared/utils'
import { CareAppointmentState } from '../care/state'
import { SRModal, SRConfirmModal } from '@sr/ui'
import { Button, BUTTON_COLORS, BUTTON_VARIANTS } from '@shared/ui'
import { TravelTimeForm } from './travel-time-form'
import { TrashCan } from '@carbon/icons-react'

interface TravelTimeProps {
  isReadOnly: boolean
  isEdit: boolean
  actionAfterSubmit: () => void
  isCoordinator: boolean
  onClose: () => void
}

interface FormData {
  startDate: string
  endDate: string
}

interface ModalContextData {
  eventId?: string
  appointmentDay?: Date | string
  startTime?: Date | string
  endTime?: Date | string
  travelType?: string
  originalAppointmentId?: string
  originalAppointmentStart?: string
  originalAppointmentEnd?: string
  currentEffectiveStart?: string
  currentEffectiveEnd?: string
  policyId?: string
}

export const TravelTime: React.FC<TravelTimeProps> = observer(
  ({ isReadOnly, isEdit, actionAfterSubmit, isCoordinator, onClose }) => {
    const { t } = useTranslation()
    const { id: socialReferentId } = useParams<{ id: string }>()
    
    const modalContext = useModalContext() as ModalContextData | null
    const {
      eventId,
      appointmentDay,
      startTime,
      endTime,
      travelType,
      originalAppointmentId,
      originalAppointmentStart,
      originalAppointmentEnd,
      currentEffectiveStart,
      currentEffectiveEnd,
      policyId,
    } = modalContext || {}

    // For travel times, always use the original appointment ID, never the travel-time ID
    const cleanEventId = originalAppointmentId || (eventId && eventId.replace(/-travel-(before|after)$/, '')) || eventId

    const {
      saveAppointment,
    } = useRouteState(CareAppointmentState, {
      eventId: cleanEventId, // Always use clean appointment ID without travel suffixes
      isEdit,
      socialReferentId,
      appointmentType: 0,
      policyId,
      isTravelTime: true, // Add this flag to identify travel time
    })

    const [isEditing, setIsEditing] = useState<boolean>(false)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false)

    const toggleEdit = () => {
      setIsEditing(!isEditing)
    }

    const onSubmit = (form: FormData) => {
      
      try {
        const { startDate, endDate } = form
        
        // Validate form data
        if (!startDate && !endDate) {
          return
        }
        
        const appointmentDate: string = typeof appointmentDay === 'string' 
          ? appointmentDay 
          : getDateFormatted(appointmentDay, 'YYYY-MM-DD')
        
        if (!appointmentDate) {
          return
        }
        

        // Use original appointment times with error handling
        let appointmentStartTime: string = '09:00'
        let appointmentEndTime: string = '10:00'
        
        try {
          if (originalAppointmentStart) {
            appointmentStartTime = getTime(originalAppointmentStart) || '09:00'
          }
          if (originalAppointmentEnd) {
            appointmentEndTime = getTime(originalAppointmentEnd) || '10:00'
          }
        } catch (error) {
          // Handle error silently
        }
          
        // Validate before calling parseCustomDate
        
        if (!appointmentStartTime || !appointmentEndTime || !appointmentDate) {
          return
        }

        let startDateParsed, endDateParsed
        try {
          startDateParsed = parseCustomDate(appointmentDate, appointmentStartTime)
          endDateParsed = parseCustomDate(appointmentDate, appointmentEndTime)
        } catch (error) {
          return
        }

        const updateData: any = {
          id: originalAppointmentId || eventId,
          startDate: startDateParsed,
          endDate: endDateParsed,
        }

        // Always send both effective times, but only modify the one we're editing
        
        // ALWAYS include both effective times in the payload
        if (travelType === 'before') {
          const travelStartTime = startDate || '08:00'
          if (travelStartTime) {
            // Update effectiveStart with new value
            updateData.effectiveStart = parseCustomDate(appointmentDate, travelStartTime)
            
            // ALWAYS include effectiveEnd - use current value or default to appointment end
            if (currentEffectiveEnd && currentEffectiveEnd !== '') {
              // Convert to ISO string if it's a Date object, otherwise use as string
              try {
                const dateValue = currentEffectiveEnd as unknown as Date
                if (dateValue && typeof dateValue.toISOString === 'function') {
                  updateData.effectiveEnd = dateValue.toISOString()
                } else {
                  updateData.effectiveEnd = currentEffectiveEnd as string
                }
              } catch {
                updateData.effectiveEnd = currentEffectiveEnd as string
              }
            } else {
              updateData.effectiveEnd = parseCustomDate(appointmentDate, appointmentEndTime)
            }
          }
        } else if (travelType === 'after') {
          const travelEndTime = endDate || '11:00'
          if (travelEndTime) {
            // ALWAYS include effectiveStart - use current value or default to appointment start
            if (currentEffectiveStart && currentEffectiveStart !== '') {
              // Convert to ISO string if it's a Date object, otherwise use as string
              try {
                const dateValue = currentEffectiveStart as unknown as Date
                if (dateValue && typeof dateValue.toISOString === 'function') {
                  updateData.effectiveStart = dateValue.toISOString()
                } else {
                  updateData.effectiveStart = currentEffectiveStart as string
                }
              } catch {
                updateData.effectiveStart = currentEffectiveStart as string
              }
            } else {
              updateData.effectiveStart = parseCustomDate(appointmentDate, appointmentStartTime)
            }
            
            // Update effectiveEnd with new value
            updateData.effectiveEnd = parseCustomDate(appointmentDate, travelEndTime)
          }
        }
        
        // ENSURE both fields are always present
        if (!updateData.effectiveStart) {
          updateData.effectiveStart = parseCustomDate(appointmentDate, appointmentStartTime)
        }
        if (!updateData.effectiveEnd) {
          updateData.effectiveEnd = parseCustomDate(appointmentDate, appointmentEndTime)
        }
        


        saveAppointment(updateData)
          .then(() => {
            actionAfterSubmit()
          })
          .catch((error: any) => {
            console.error('Error saving travel time', error)
          })
      } catch (error) {
        console.error('Error building travel time payload', error)
      }
    }

    const onDelete = () => {
      
      try {
        const appointmentDate: string = typeof appointmentDay === 'string' 
          ? appointmentDay 
          : getDateFormatted(appointmentDay, 'YYYY-MM-DD')
        
        // Use original appointment times for delete
        const appointmentStartTime = originalAppointmentStart 
          ? getTime(originalAppointmentStart) || '09:00'
          : '09:00'
        const appointmentEndTime = originalAppointmentEnd 
          ? getTime(originalAppointmentEnd) || '10:00'
          : '10:00'
        
        const updateData: any = {
          id: originalAppointmentId || eventId,
          startDate: parseCustomDate(appointmentDate, appointmentStartTime),
          endDate: parseCustomDate(appointmentDate, appointmentEndTime),
        }
        
        // Only reset the effective time we're deleting, keep the other one unchanged
        if (travelType === 'before') {
          // Deleting "before" travel time - set effectiveStart to appointment start
          updateData.effectiveStart = parseCustomDate(appointmentDate, appointmentStartTime)
          
          // Keep current effectiveEnd unchanged (preserve "after" travel if it exists)
          if (currentEffectiveEnd && currentEffectiveEnd !== '') {
            try {
              const dateValue = currentEffectiveEnd as unknown as Date
              if (dateValue && typeof dateValue.toISOString === 'function') {
                updateData.effectiveEnd = dateValue.toISOString()
              } else {
                updateData.effectiveEnd = currentEffectiveEnd as string
              }
            } catch {
              updateData.effectiveEnd = currentEffectiveEnd as string
            }
          } else {
            // If no current effectiveEnd, set to appointment end
            updateData.effectiveEnd = parseCustomDate(appointmentDate, appointmentEndTime)
          }
        } else if (travelType === 'after') {
          // Deleting "after" travel time - set effectiveEnd to appointment end  
          updateData.effectiveEnd = parseCustomDate(appointmentDate, appointmentEndTime)
          
          // Keep current effectiveStart unchanged (preserve "before" travel if it exists)
          if (currentEffectiveStart && currentEffectiveStart !== '') {
            try {
              const dateValue = currentEffectiveStart as unknown as Date
              if (dateValue && typeof dateValue.toISOString === 'function') {
                updateData.effectiveStart = dateValue.toISOString()
              } else {
                updateData.effectiveStart = currentEffectiveStart as string
              }
            } catch {
              updateData.effectiveStart = currentEffectiveStart as string
            }
          } else {
            // If no current effectiveStart, set to appointment start
            updateData.effectiveStart = parseCustomDate(appointmentDate, appointmentStartTime)
          }
        }


        saveAppointment(updateData)
          .then(() => {
            actionAfterSubmit()
          })
          .catch((error: any) => {
            console.error('Error deleting travel time', error)
          })
      } catch (error) {
        console.error('Error building delete travel time payload', error)
      }
    }

    const handleDelete = () => {
      setShowDeleteConfirm(true)
    }

    const confirmDelete = () => {
      onDelete()
      setShowDeleteConfirm(false)
    }

    const cancelDelete = () => {
      setShowDeleteConfirm(false)
    }


    // Helper function to safely get time string - if it's already a string in HH:mm format, use it directly
    const safeGetTime = (timeValue: any): string => {
      if (!timeValue) return ''
      
      // If it's already a string in HH:mm format, use it directly
      if (typeof timeValue === 'string' && /^\d{2}:\d{2}$/.test(timeValue)) {
        return timeValue
      }
      
      // Otherwise, try to use getTime function
      try {
        const formatted = getTime(timeValue)
        return formatted === 'Fecha inválida' ? '' : formatted
      } catch {
        return ''
      }
    }

    // Safe default values - set the correct time for the editable field based on travel type
    const defaultValues: FormData = {
      startDate: travelType === 'before' 
        ? (safeGetTime(startTime) || '09:00')  // For "before" travel, startTime is the editable field
        : '09:00',  // For "after" travel, startDate is not editable, so default value doesn't matter
      endDate: travelType === 'after' 
        ? (safeGetTime(endTime) || '10:00')    // For "after" travel, endTime is the editable field
        : '10:00',  // For "before" travel, endDate is not editable, so default value doesn't matter
    }


    // Safe appointment times for display - use original appointment times
    let appointmentStartTime: string = '09:00'
    let appointmentEndTime: string = '10:00'
    
    try {
      if (originalAppointmentStart) {
        appointmentStartTime = getTime(originalAppointmentStart) || '09:00'
      } else if (travelType === 'before' && endTime) {
        appointmentStartTime = getTime(endTime) || '09:00'
      }
      
      if (originalAppointmentEnd) {
        appointmentEndTime = getTime(originalAppointmentEnd) || '10:00'
      } else if (travelType === 'after' && startTime) {
        appointmentEndTime = getTime(startTime) || '10:00'
      }
    } catch (error) {
      appointmentStartTime = '09:00'
      appointmentEndTime = '10:00'
    }

    const travelTypeText: string = travelType === 'before' 
      ? t('agenda.travelTime.beforeAppointment')
      : t('agenda.travelTime.afterAppointment')


    if (showDeleteConfirm) {
      return (
        <SRConfirmModal
          onAction={confirmDelete}
          onClose={cancelDelete}
          title={t('agenda.travelTime.deleteTitle')}
          onConfirmText="agenda.travelTime.deleteButtonConfirm"
          onCancelText="common:cancel"
        >
          <div>{t('agenda.travelTime.deleteConfirmation')}</div>
        </SRConfirmModal>
      )
    }

    return (
      <SRModal 
        onClose={onClose}
        title={`${t('agenda.travelTime.editTitle')} - ${travelTypeText}`}
        className="max-w-3xl"
      >
        <div className="p-6">
          <TravelTimeForm
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            isReadOnly={isReadOnly || (!isEditing && isEdit)}
            isEdit={isEdit}
            isEditing={isEditing}
            travelType={travelType || 'before'}
            appointmentStartTime={appointmentStartTime}
            appointmentEndTime={appointmentEndTime}
            currentEffectiveStart={currentEffectiveStart}
            currentEffectiveEnd={currentEffectiveEnd}
          />
          
          <div className="flex justify-between mt-6">
            <div>
              {isEdit && (
                <Button
                  color={BUTTON_COLORS.primary}
                  variant={BUTTON_VARIANTS.text}
                  onClick={handleDelete}
                  disabled={isReadOnly}
                  className="text-[#004039] px-4 py-2 rounded-md font-medium border-none flex items-center justify-start gap-2 h-10 min-w-24"
                >
                  
                  {t('agenda.travelTime.delete')}
                  <TrashCan size={24} />
                </Button>
              )}
            </div>
            
            <div className="flex gap-3">
              <Button
                color={BUTTON_COLORS.primary}
                variant={BUTTON_VARIANTS.outlined}
                onClick={onClose}
                className="border-[#004039] text-[#004039] px-4 py-2 rounded-md font-medium h-10 min-w-24 flex items-center justify-center"
              >
                {t('common:cancel')}
              </Button>
              
              {isEdit && !isEditing && !isReadOnly && (
                <Button
                  onClick={toggleEdit}
                  className="bg-[#004039] border-[#004039] text-white px-4 py-2 rounded-md font-medium h-10 min-w-24 flex items-center justify-center"
                >
                  {t('common:edit')}
                </Button>
              )}
              
              {(!isEdit || isEditing) && !isReadOnly && (
                <Button
                  onClick={() => {
                    const form = document.getElementById('travel-time-form') as HTMLFormElement
                    if (form) {
                      form.requestSubmit()
                    }
                  }}
                  className="bg-[#004039] border-[#004039] text-white px-4 py-2 rounded-md font-medium h-10 min-w-24 flex items-center justify-center"
                >
                  {t('common:save')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </SRModal>
    )
  }
)
